// ============================================================================
// @orgazone/core-loyalty — moteur de FIDÉLITÉ (cœur DÉDIÉ, extrait de `crm`). Pur TS, aucune dépendance hors des
// cœurs (l'arithmétique d'argent vient de `core-money`), persistance INJECTÉE (le grand livre `loyalty_ledger` est
// lu/écrit par l'app).
// UTILITÉ : centraliser le CALCUL fidélité — acquisition (earn), solde (balance), consommation
// (redeem = REMISE), paliers (computeTier) — même règle sur caisse retail/CHR + BO + ERP, golden-testée
// (rétrograder à tort = incident client). Doctrine numérique §24.6bis A : cashback/valeur = CENTIMES
// entiers, arrondi HALF-UP §30.3. Réf. SPEC tronc §24.6ter + retail §12.1.
// ⚠️ Un gain fidélité CONSOMMÉ = une REMISE commerciale (→ core-pricing, base réduite + TVA prorata),
//    JAMAIS un moyen de paiement (≠ avoir/chèque cadeau qui sont des titres).
// V1 = mode CASHBACK (gains cumulés en € → remise directe). Le mode « bon d'achat » (émission d'un
//    bon de remise daté via core-vouchers + impression au seuil) viendra en surcouche.
// ============================================================================

export type LoyaltyMode = 'cashback' | 'bon_achat';
export type LoyaltyTier = 'silver' | 'gold' | 'platinium';

/** Arrondi HALF-UP §30.3 appliqué à la MAGNITUDE puis re-signé → clawback SYMÉTRIQUE : earn(−x) = −earn(x). */
function scaleHalfUp(cents: number, rateBp: number): number {
  const sign = cents < 0 ? -1 : 1;
  const mag = Math.abs(cents) * rateBp; // rateBp = points de base (10000 = 100 %)
  return sign * Math.floor((mag + 5000) / 10000);
}

// ── ACQUISITION (earn) ────────────────────────────────────────────────────
/** Règles d'acquisition (issues des Réglages fidélité §12.1, BO-driven). */
export interface EarnRules {
  /** Taux de conversion en POINTS DE BASE (ex. 200 = 2 % du montant crédité en cagnotte €). */
  rateBp: number;
  /** Base de calcul : TOUT le panier, ou seulement les lignes NON remisées (défaut Sliderz : pas de gain sur promo/solde). */
  base: 'all' | 'nonDiscounted';
}
/** Une ligne de vente vue par la fidélité (net déjà calculé par core-pricing). */
export interface EarnLine {
  /** Net TTC de la ligne, en centimes, SIGNÉ (retour = négatif → clawback). */
  netCents: number;
  /** La ligne porte-t-elle une remise (ligne, globale ou fidélité) ? — exclue si base='nonDiscounted'. */
  discounted: boolean;
  /** Ligne HORS fidélité par nature (titre cadeau/acompte, prestation offerte…). */
  excluded?: boolean;
}
/** Gain acquis (centimes) pour une vente. Cashback = rateBp × base. Un RETOUR (nets négatifs) ⇒ clawback négatif symétrique. */
export function earn(lines: EarnLine[], rules: EarnRules): number {
  const baseCents = lines
    .filter((l) => !l.excluded && (rules.base === 'all' || !l.discounted))
    .reduce((s, l) => s + l.netCents, 0);
  return scaleHalfUp(baseCents, rules.rateBp);
}

// ── SOLDE (balance) — DÉRIVÉ du grand livre append-only, jamais un compteur écrit à part ──
/**
 * Une écriture du grand livre `loyalty_ledger`.
 *
 * ⚠️ DEUX COLONNES SORTENT DE LA CAGNOTTE, PAS UNE. Le « cashback » déduit la remise à la caisse
 * (`debitCents`), le « bon d'achat » convertit la cagnotte en titre remis au client
 * (`voucherIssuedCents`) — et dans ce second cas le débit reste à ZÉRO. Ne regarder que le débit fait
 * croire à une cagnotte que le client a déjà dépensée.
 * ⚠️ UNE REMISE N'EST JAMAIS ÉCRITE DANS LES DEUX À LA FOIS — mesuré sur deux bases réelles (5 000 et
 * 12 000 écritures) : 0 ligne porte un débit ET un bon. C'est ce qui rend leur SOMME juste, sans
 * double compte. Un producteur qui en écrirait deux pour un même geste ferait sortir la cagnotte deux
 * fois : la règle tient au moment de l'écriture, elle ne peut pas se rattraper à la lecture.
 */
export interface LedgerEntry {
  creditCents: number; // gain acquis (+)
  debitCents: number;  // gain déduit à la caisse — cashback (+)
  /** Cagnotte CONVERTIE en bon d'achat ; ⚠️ sort de la cagnotte, tout autant qu'un débit. */
  voucherIssuedCents?: number;
  /** Montant d'achat porté par l'écriture — un gain sur vente en porte toujours un. */
  purchaseAmountCents?: number;
  /** Montant rendu (retour, annulation) porté par l'écriture. */
  returnAmountCents?: number;
}

/**
 * UNE REMISE RENDUE — le crédit qu'écrit un retour ou une annulation pour rendre au client la remise fidélité qu'il avait
 * consommée. ⚠️ CE N'EST PAS UN GAIN : comptée en « acquis », elle gonflait ce que le programme a rapporté, et le détail de
 * la cagnotte la nommait « gain sur vente » (F11). Elle ANNULE une consommation.
 * ⚠️ RECONNUE À SES MONTANTS — un montant rendu, aucun montant d'achat —, faute de colonne de nature au grand livre : un
 *    gain sur vente porte toujours son montant d'achat. Une écriture qui ne dit pas ses montants n'est jamais prise pour
 *    une remise rendue : sans eux, on lit comme avant.
 */
export function isReturnedDiscount(e: LedgerEntry): boolean {
  return (e.creditCents || 0) > 0 && (e.returnAmountCents ?? 0) > 0 && (e.purchaseAmountCents ?? 0) === 0;
}

/**
 * CE QUI EST SORTI DE LA CAGNOTTE sur une écriture — la définition unique de « rendu au client ».
 *
 * ⚠️ ELLE EXISTE POUR N'ÊTRE ÉCRITE QU'UNE FOIS : le solde, la répartition réseau et les totaux de
 * période s'en servent tous les trois. Recopiée à trois endroits, elle aurait fini par diverger, et
 * deux écrans auraient annoncé deux cagnottes pour le même client.
 */
export function redeemedCents(e: LedgerEntry): number {
  return (e.debitCents || 0) + (e.voucherIssuedCents || 0);
}

/**
 * Cagnotte du client = Σ crédits − Σ sorties (centimes) — réconciliation multi-caisses déterministe.
 *
 * ⚠️ LE BON D'ACHAT VIDE LA CAGNOTTE, et l'oublier crédite le client d'argent qu'il a déjà dépensé.
 * Vérifié sur une base réelle, sans une seule exception : sur les 198 bons émis, le montant du bon
 * vaut EXACTEMENT le solde accumulé juste avant lui (198/198, ni partiel ni supérieur). Compter le
 * seul débit y aurait laissé 2 685,33 € de cagnotte fantôme sur 155 clients — jusqu'à 253,36 € pour
 * un seul.
 */
export function balance(entries: LedgerEntry[]): number {
  return entries.reduce((s, e) => s + (e.creditCents || 0) - redeemedCents(e), 0);
}

/**
 * LA MÊME RÈGLE, EN SQL — pour les deux bouts qui la calculent sur une base plutôt qu'en mémoire.
 *
 * ⚠️ ELLE EST ICI PARCE QU'ELLE ÉTAIT ÉCRITE DEUX FOIS : le serveur re-dérivait la cagnotte d'un côté,
 * la caisse tenait la sienne par des deltas de l'autre. Deux calculs du même argent finissent par ne
 * plus dire la même chose, et c'est le client au comptoir qui l'apprend. Une seule source, deux
 * appelants.
 *
 * ⚠️ LE DÉBIT EST LU EN VALEUR ABSOLUE, le crédit NON. Les deux conventions de signe cohabitent sur la
 * même table : la reprise legacy a longtemps écrit les débits en négatif, tandis qu'un crédit négatif
 * est un fait métier — la reprise d'un gain sur un retour. Les traiter pareil fausserait l'un ou l'autre.
 *
 * ⚠️ LE TEXTE EST COMMUN AUX DEUX DIALECTES (Postgres au siège, SQLite en caisse) : `sum` et `abs`
 * s'y écrivent à l'identique, et les guillemets doubles y désignent un identifiant dans les deux. Il
 * ne faut donc y introduire aucune fonction propre à un moteur.
 *
 * ⚠️ SUR ZÉRO LIGNE, `sum()` REND `NULL` — et l'expression entière avec lui. C'est à L'APPELANT de
 * poser le `COALESCE` autour, parce que lui seul sait ce que « aucun mouvement » doit valoir chez lui.
 * L'enterrer ici rendrait la chaîne moins lisible sans rendre personne plus sûr.
 */
export const LOYALTY_BALANCE_SQL =
  'sum("credit_cents") - sum(abs("debit_cents")) - sum("voucher_issued_cents")';

export interface LoyaltyPeriodTotals {
  /** Cagnotte ACQUISE sur la période (Σ crédits). */
  earnedCents: number;
  /** Cagnotte déduite DIRECTEMENT en caisse (Σ débits) — le schéma « cashback ». */
  spentCents: number;
  /** Cagnotte convertie en BON D'ACHAT remis au client — le schéma « bon d'achat ». */
  vouchersCents: number;
  /**
   * TOTAL RENDU AU CLIENT sur la période = déduit + converti en bons.
   *
   * ⚠️ C'EST CE CHIFFRE QUE LE COMMERÇANT APPELLE « remises de fidélité », et il ne se lit pas sur le
   * seul débit. Mesuré sur une base réelle : 192,66 € de débits pour **2 646,78 € de bons émis** —
   * n'afficher que le débit annonçait 1,4 % d'usage là où le programme en rend près de 20 %. Les deux
   * schémas coexistent dans le même magasin (« cashback » déduit à la caisse, « bon d'achat » remet un
   * titre), et un seul des deux ne raconte pas le programme.
   * ⚠️ AUCUN DOUBLE COMPTE, PARCE QUE LES DEUX NE COEXISTENT JAMAIS : sur deux bases réelles (5 000 et
   * 12 000 écritures), 0 ligne porte un débit ET un bon. La ligne de bon ne remplit pas la colonne
   * `debit` — mais elle vide bel et bien la cagnotte (le bon vaut EXACTEMENT le solde d'avant,
   * 198 fois sur 198), et c'est pourquoi `balance` la déduit aussi. Rendu et solde partagent donc la
   * MÊME définition de sortie (`redeemedCents`) : `balance = Σ crédits − Σ rendu`.
   */
  returnedCents: number;
  /**
   * Part RENDUE de ce qui a été acquis, en points de base. `null` = rien d'acquis à rapporter.
   * ⚠️ Rapportée à `returnedCents`, pas au seul débit : voir ci-dessus.
   */
  usageBp: number | null;
}

/**
 * CE QU'UNE PÉRIODE DIT DE LA FIDÉLITÉ — acquis, consommé, bons émis, et le rapport des deux.
 *
 * ⚠️ ELLE VIT DANS LE CŒUR, PAS DANS UNE REQUÊTE SQL, et c'est le point : le tableau de bord, la
 * caisse et le futur module de statistiques doivent compter la fidélité de la MÊME façon. Écrite en
 * SQL côté back-office, la définition serait rejouée ailleurs — et deux écrans finiraient par
 * annoncer deux cagnottes pour la même semaine.
 *
 * ⚠️ DEUX SCHÉMAS COEXISTENT, et ne pas les additionner fausse tout : le « cashback » DÉDUIT la remise
 * à la caisse (`debitCents`), le « bon d'achat » REMET un titre au client (`voucherIssuedCents`). Ce
 * que le commerçant appelle « remises de fidélité » est la somme des deux — c'est `returnedCents`.
 *
 * ⚠️ `usageBp` VAUT `null` ET NON ZÉRO quand rien n'a été acquis : « 0 % consommé » affirmerait qu'un
 * programme tourne sans être utilisé, là où il ne s'est rien passé du tout.
 *
 * ⚠️ La part peut DÉPASSER 100 % : on consomme une cagnotte accumulée AVANT la période. Le rapport
 * n'est donc pas borné — le plafonner masquerait précisément les périodes où les clients viennent
 * dépenser ce qu'ils ont mis de côté.
 */
export function loyaltyPeriodTotals(entries: LedgerEntry[]): LoyaltyPeriodTotals {
  let earnedCents = 0;
  let spentCents = 0;
  let vouchersCents = 0;
  for (const e of entries) {
    // ⚠️ UNE REMISE RENDUE ANNULE UNE CONSOMMATION : elle se retranche de « consommé », jamais ajoutée à « acquis ».
    if (isReturnedDiscount(e)) spentCents -= e.creditCents;
    else earnedCents += e.creditCents || 0;
    spentCents += e.debitCents || 0;
    vouchersCents += e.voucherIssuedCents || 0;
  }
  const returnedCents = spentCents + vouchersCents; // = Σ redeemedCents — même définition que le solde
  return {
    earnedCents,
    spentCents,
    vouchersCents,
    returnedCents,
    usageBp: earnedCents > 0 ? Math.round((returnedCents / earnedCents) * 10000) : null,
  };
}

// ── RÉPARTITION RÉSEAU (balanceByGln) — sous-soldes par GLN, consommation FIFO ──────────────────
// RÈGLE RÉSEAU §24.6ter : la fidélité appartient au RÉSEAU (pas au magasin). Une dépense de points
// consomme le crédit du GLN qui les a accordés, LE PLUS ANCIEN D'ABORD (FIFO, tous GLN confondus).
// Sans elle, une DÉFUSION laisserait un magasin à solde NÉGATIF (points qu'il n'a jamais accordés).
// Le solde GLOBAL (`balance`) est INCHANGÉ (= Σ des sous-soldes) ; ce qui change, c'est leur RÉPARTITION.
/** Écriture du grand livre CONSCIENTE du GLN (chaque ligne `loyalty_ledger` porte son `gln` + son horodatage). */
export interface GlnLedgerEvent extends LedgerEntry {
  /** GLN émetteur du crédit (magasin qui a accordé les points) ; sur un débit, GLN où la dépense a lieu (informatif). */
  gln: string;
  /** Horodatage (ms) — donne l'ordre FIFO de consommation. */
  at: number;
}
/**
 * Sous-soldes par GLN après consommation FIFO (le plus ancien crédit d'abord, tous GLN confondus).
 * Débits ET clawbacks (crédit négatif d'un retour) grignotent les lots les plus anciens ⇒ chaque
 * sous-solde reste ≥ 0 et une défusion redonne à chaque magasin SES points restants. Σ(valeurs) = `balance`.
 */
export function balanceByGln(events: GlnLedgerEvent[]): Map<string, number> {
  const ordered = events.map((e, i) => ({ e, i })).sort((a, b) => a.e.at - b.e.at || a.i - b.i);
  const lots: { gln: string; remaining: number }[] = []; // file FIFO des crédits encore garnis (par ancienneté)
  for (const { e } of ordered) {
    const add = Math.max(0, e.creditCents || 0);                       // gain accordé (crédit +)
    // ⚠️ Un BON D'ACHAT grignote les lots au même titre qu'un débit : il sort de la cagnotte. L'omettre
    //    laisserait des points au magasin émetteur alors que le client les a déjà emportés.
    const reduce = redeemedCents(e) + Math.max(0, -(e.creditCents || 0)); // sortie (débit + bon) + clawback
    if (add > 0) lots.push({ gln: e.gln, remaining: add });
    let need = reduce;
    for (const lot of lots) {
      if (need <= 0) break;
      if (lot.remaining <= 0) continue;
      const take = Math.min(lot.remaining, need);
      lot.remaining -= take;
      need -= take;
    }
    // need > 0 ⇒ surconsommation (solde global insuffisant) : bornée en amont (redeem clampé au solde) ;
    //           on n'invente jamais de sous-solde négatif.
  }
  const byGln = new Map<string, number>();
  for (const lot of lots) if (lot.remaining > 0) byGln.set(lot.gln, (byGln.get(lot.gln) ?? 0) + lot.remaining);
  return byGln;
}

// ── CONSOMMATION (redeem) — le gain utilisé devient une REMISE (appliquée ensuite par core-pricing) ──
/** Règles de consommation (Réglages §12.1). En cashback `minRedeemCents = 0` (aucun minimum). */
export interface RedeemRules {
  /** Seuil « utilisation des gains à partir de X € » en centimes (bon d'achat) ; 0 en cashback. */
  minRedeemCents: number;
}
/** Remise fidélité MAXIMALE utilisable : 0 sous le seuil, sinon bornée au solde ET au total à payer (une remise ne dépasse jamais le dû). */
export function maxRedeemable(balanceCents: number, saleTotalCents: number, rules: RedeemRules): number {
  if (balanceCents < rules.minRedeemCents) return 0;
  return Math.max(0, Math.min(balanceCents, saleTotalCents));
}
/** Remise fidélité RÉELLEMENT appliquée pour un montant demandé (clampée au max utilisable ; jamais négative). */
export function redeem(requestedCents: number, balanceCents: number, saleTotalCents: number, rules: RedeemRules): number {
  return Math.max(0, Math.min(requestedCents, maxRedeemable(balanceCents, saleTotalCents, rules)));
}

// ── PALIERS (computeTier) — fenêtre glissante + anti-rétrogradation (hystérésis) ──────────
// ⚠️ AUTORITÉ SERVEUR (réconciliation multi-caisses) → la tablette LIT `client.tier`, ne recalcule pas.
// Le cœur est PUR (golden-testé, portable serveur/BO/caisse provisoire) et ne rétrograde JAMAIS à tort.
export interface TierThreshold { tier: LoyaltyTier; minCents: number; }
export interface TierRules {
  /** Fenêtre glissante en jours (défaut produit : 365). */
  windowDays: number;
  /** Seuils par palier (montant d'achat cumulé sur la fenêtre) — ordre quelconque, trié en interne. */
  thresholds: TierThreshold[];
  /** Hystérésis anti-rétrogradation (centimes) : on ne descend que sous (seuil du palier courant − grâce). */
  graceCents?: number;
  /**
   * PROTECTION DU STATUT en JOURS, ANCRÉE SUR LE PASSAGE au palier. 0/absent = aucune.
   *
   * **La règle, telle qu'elle a été arrêtée (décision utilisateur 2026-08-31)** : « un client qui passe
   * en Gold ne pourra pas repasser en Silver pendant cette durée, même si le calcul de son cumul
   * retombe sous le palier ». La durée court donc **depuis la PROMOTION**, pas depuis la chute.
   *
   * ⚠️ DEUX PÉRIODES QU'IL NE FAUT PAS CONFONDRE : `windowDays` est la fenêtre du **cumul** (sur quoi
   * on calcule) ; `protectDays` est la garantie du **statut** (combien de temps il tient). Elles se
   * règlent séparément et ne parlent pas de la même chose.
   *
   * ⚠️ POURQUOI UNE SECONDE PROTECTION, ET EN JOURS. `graceCents` est une hystérésis de MONTANT ; les
   * écrans, eux, promettent depuis toujours une DURÉE. Aucune conversion n'existe entre des centimes et
   * des jours : le réglage `fid.protect_days` ne pouvait être branché sur RIEN (audit fidélité
   * 2026-08-31, F8). Un réglage offert que le moteur ne sait pas lire est une option qui n'existe pas.
   *
   * ⚠️ AUCUNE DATE D'OBTENTION N'EST STOCKÉE, et c'est ce qui garde le calcul rejouable : les
   * promotions se REJOUENT depuis l'historique d'achats. Un état persisté aurait dû être tenu juste, et
   * une valeur fausse s'y serait entretenue toute seule.
   *
   * ⚠️ CE QU'ELLE COÛTE, ET C'EST ASSUMÉ : un client qui qualifie sans interruption pendant trois ans
   * n'est plus protégé (sa protection a expiré un an après son passage) — le jour où son cumul retombe,
   * il redescend. C'est bien ce que « protéger le passage » veut dire ; protéger la chute serait l'autre
   * règle, celle qui n'a pas été retenue.
   */
  protectDays?: number;
}
export interface TierPurchase { eventAt: number; purchaseAmountCents: number; }
/** Palier du client d'après ses achats sur la fenêtre glissante, avec hystérésis (garde le palier courant tant qu'on reste dans la grâce). */
export function computeTier(purchases: TierPurchase[], now: number, currentTier: LoyaltyTier | null, rules: TierRules): LoyaltyTier {
  const sorted = [...rules.thresholds].sort((a, b) => a.minCents - b.minCents);
  const sumOverWindow = (instant: number): number => {
    const depuis = instant - rules.windowDays * 86400000;
    return purchases
      .filter((p) => p.eventAt >= depuis && p.eventAt <= instant)
      .reduce((s, p) => s + p.purchaseAmountCents, 0);
  };
  const tierOf = (sum: number): LoyaltyTier => {
    let t: LoyaltyTier = sorted[0]?.tier ?? 'silver';
    for (const th of sorted) if (sum >= th.minCents) t = th.tier;
    return t;
  };
  const rankOf = (t: LoyaltyTier): number => sorted.findIndex((s) => s.tier === t);

  const windowSum = sumOverWindow(now);
  // Palier « brut » = plus haut seuil atteint sur la fenêtre, AUJOURD'HUI.
  let raw = tierOf(windowSum);

  // ── PROTECTION DU STATUT (en JOURS), ANCRÉE SUR LE PASSAGE ───────────────────────────────────────
  // On REJOUE l'histoire du client : à chaque instant où le classement peut bouger, on promeut si le
  // cumul le permet (en armant la protection), et on ne rétrograde que si la protection en cours a
  // expiré. Le palier au terme du rejeu est celui d'aujourd'hui.
  //
  // ⚠️ LES INSTANTS CANDIDATS SONT EXACTS, PAS ÉCHANTILLONNÉS. Trois choses seulement peuvent changer
  // le verdict, et on les connaît toutes d'avance : un ACHAT (le cumul monte), un achat qui SORT de la
  // fenêtre (le cumul baisse), une PROTECTION qui EXPIRE (une rétrogradation devient possible). Entre
  // deux de ces instants, rien ne bouge — balayer le temps donnerait le même résultat pour un coût
  // sans rapport.
  // ⚠️ LES EXPIRATIONS SONT DANS LA LISTE, et il le faut : sans elles, une rétrogradation due entre
  // deux achats serait reportée au suivant — et un achat qui aurait dû être une NOUVELLE promotion
  // (donc réarmer la protection) passerait pour la simple continuation d'un palier jamais quitté.
  const protectDays = rules.protectDays ?? 0;
  if (protectDays > 0 && sorted.length > 0) {
    const instants = [...new Set([
      ...purchases.map((p) => p.eventAt),
      ...purchases.map((p) => p.eventAt + rules.windowDays * 86400000 + 1), // juste APRÈS la sortie de fenêtre
      ...purchases.map((p) => p.eventAt + protectDays * 86400000),          // fin de protection possible
      now,
    ])].filter((t) => t <= now).sort((a, b) => a - b);

    let current: LoyaltyTier = sorted[0]!.tier;
    let protectedUntil = -Infinity;
    for (const t of instants) {
      const rawTier = tierOf(sumOverWindow(t));
      if (rankOf(rawTier) > rankOf(current)) {
        // PASSAGE au palier supérieur : c'est LUI que la durée protège, et elle repart de cet instant.
        current = rawTier;
        protectedUntil = t + protectDays * 86400000;
      } else if (rankOf(rawTier) < rankOf(current) && t >= protectedUntil) {
        // La protection a expiré : le cumul reprend la main. Une rétrogradation n'arme rien.
        current = rawTier;
      }
    }
    raw = current;
  }

  // Anti-rétrogradation par MONTANT : si le brut passe SOUS le palier courant, on le garde tant qu'on
  // est dans la grâce. ⚠️ Les deux protections se cumulent — ce sont deux garde-fous distincts contre
  // la même chose (rétrograder à tort), et prendre le plus favorable est la seule lecture qui ne
  // surprenne pas un client.
  if (currentTier) {
    const curIdx = rankOf(currentTier);
    const rawIdx = rankOf(raw);
    const cur = curIdx >= 0 ? sorted[curIdx] : undefined;
    if (cur && rawIdx < curIdx && windowSum >= cur.minCents - (rules.graceCents ?? 0)) {
      return currentTier;
    }
  }
  return raw;
}

// ── CE QUE LE CLIENT VOIT, ET CE QUE LE COMMERÇANT VOIT ─────────────────────────────────────────
/**
 * LE SOLDE TEL QUE LE CLIENT PEUT LE VOIR — jamais négatif.
 *
 * **La règle (décision utilisateur 2026-08-31)** : « le client ne doit JAMAIS voir un solde négatif,
 * il doit voir 0 là où il est susceptible de le voir — le wallet, les canaux de communication
 * marketing. Le commerçant, lui, doit voir une cagnotte négative et avoir l'explication. »
 *
 * ⚠️ POURQUOI UNE CAGNOTTE DEVIENT NÉGATIVE, ET POURQUOI CE N'EST PAS UN BUG. Le client dépense toute
 * sa cagnotte (ou un bon lui a été imprimé), puis rend un article : la reprise du gain dépasse ce qui
 * restait. Le solde est alors JUSTE — il dit que le client doit des points — mais il est illisible
 * pour lui : « −4,20 € » sur son wallet ne veut rien dire, et donne l'impression d'une dette.
 *
 * ⚠️ CE N'EST PAS UN ARRONDI COSMÉTIQUE : les deux nombres coexistent, et c'est voulu. Le commerçant
 * garde le VRAI (`balance`), la seule information qui lui permette de comprendre et d'agir ; le client
 * reçoit ce qu'il peut lire. Masquer au commerçant lui retirerait le seul indice de l'anomalie ;
 * montrer au client une dette de points qu'il n'a jamais contractée n'aiderait personne.
 */
export function customerFacingBalance(balanceCents: number): number {
  return Math.max(0, balanceCents);
}

/**
 * POURQUOI une cagnotte est négative — la CAUSE, en un code que chaque écran dit dans sa langue.
 * · `voucher_then_return` — un bon d'achat a été émis, puis un article rendu ;
 * · `spend_then_return`   — des gains ont été utilisés, puis un article rendu ;
 * · `offline_overspend`   — des gains ont été utilisés au-delà du solde, par une caisse qui ne
 *                            connaissait pas encore le dernier mouvement (hors ligne) ;
 * · `unknown`             — les mouvements disponibles ne suffisent pas à le dire.
 */
export type NegativeBalanceCause = 'voucher_then_return' | 'spend_then_return' | 'offline_overspend' | 'unknown';

/**
 * CE QUE CHAQUE CAUSE VEUT DIRE, EN PEU DE MOTS (décision utilisateur : « de manière très succincte »).
 *
 * ⚠️ LE FRANÇAIS EST LA CLÉ : le back-office le traduit au rendu, la caisse l'affiche tel quel. Écrite
 *    ici et nulle part ailleurs, la phrase ne peut pas dire deux choses selon l'écran — un nombre
 *    négatif sans explication passe pour un défaut d'affichage, et l'opérateur « corrige » la fiche.
 */
export const NEGATIVE_BALANCE_CAUSE_TEXT: Readonly<Record<NegativeBalanceCause, string>> = {
  voucher_then_return: 'Bon d’achat émis, puis article rendu.',
  spend_then_return: 'Gains utilisés, puis article rendu.',
  offline_overspend: 'Gains utilisés hors ligne, au-delà du solde.',
  unknown: 'Cause hors des mouvements visibles ici.',
};

/** Un mouvement DATÉ du grand livre — les trois colonnes de montant, et son instant. */
export interface LedgerMovement extends LedgerEntry {
  at: number;
}

/**
 * LA CAUSE d'une cagnotte négative — `null` si elle ne l'est pas.
 *
 * ⚠️ ON REMONTE LE TEMPS À PARTIR DU SOLDE RÉEL, au lieu de rejouer depuis le premier mouvement : le
 *    solde après le dernier mouvement EST `balanceCents`, et défaire chaque mouvement redonne le solde
 *    d'avant. Le mouvement qui a fait passer SOUS zéro est le premier rencontré dont le solde d'avant
 *    était positif ou nul. Rejouer depuis le début exigerait TOUT le grand livre — le back-office n'en
 *    lit que les dernières lignes, et c'est suffisant ainsi.
 * ⚠️ CE QUI A FAIT PASSER SOUS ZÉRO DIT LA CAUSE :
 *    · une SORTIE (gains utilisés, bon émis) plus grande que le solde d'alors : seule une caisse qui
 *      ignorait le dernier mouvement peut l'avoir faite, l'écran bornant la remise au solde connu ;
 *    · sinon, une REPRISE de gain (retour d'article) — le seul mouvement sans sortie qui fasse baisser
 *      le solde. Ce qui l'a précédé de plus près dit pourquoi il ne restait rien à reprendre.
 * ⚠️ `unknown` N'EST PAS UNE ERREUR, ET LA CAUSE N'EST JAMAIS INVENTÉE : le passage sous zéro est plus
 *    ancien que les mouvements fournis, ou ceux-ci ne racontent pas tout.
 *
 * CONTRAT : `movements` = les DERNIERS mouvements du client, SANS TROU (une fenêtre coupée côté ancien
 * convient ; des mouvements manquants au milieu, non). ⚠️ Une caisse ne détient que SES écritures :
 * elle vérifie d'abord que leur somme redonne le solde, sinon la remontée attribuerait la cause à un
 * mouvement qui n'y est pour rien. Débits en MAGNITUDE (le contrat de tout le module). À instant
 * égal, l'ordre reçu fait foi, le plus ancien d'abord.
 */
export function negativeBalanceCause(movements: readonly LedgerMovement[], balanceCents: number): NegativeBalanceCause | null {
  if (balanceCents >= 0) return null;
  // Du plus récent au plus ancien — le tri est stable, donc à instant égal l'ordre reçu est gardé.
  const recentFirst = [...movements].sort((a, b) => a.at - b.at).reverse();
  let after = balanceCents;
  for (let k = 0; k < recentFirst.length; k += 1) {
    const m = recentFirst[k]!;
    const before = after - (m.creditCents - redeemedCents(m));
    if (before >= 0) {
      if (redeemedCents(m) > 0) return 'offline_overspend';
      const spent = recentFirst.slice(k + 1).find((p) => redeemedCents(p) > 0);
      if (!spent) return 'unknown';
      return (spent.voucherIssuedCents ?? 0) > 0 ? 'voucher_then_return' : 'spend_then_return';
    }
    after = before;
  }
  return 'unknown';
}

/**
 * LA NATURE D'UN CRÉDIT POSÉ À LA MAIN depuis la fiche client (colonne `entry_kind` du grand livre) — décision
 * utilisateur : un crédit seulement, sous le droit « Ajuster la cagnotte à la main », tracé à son auteur.
 * ⚠️ SES MONTANTS NE LE DISENT PAS : un crédit sans vente se lirait « Bonus de bienvenue », d'où cette nature écrite.
 * ⚠️ CE N'EST PAS UN ACHAT : son montant d'achat est nul, donc il ne compte ni dans le cumul qui fixe le palier, ni dans
 *    les gains sur vente d'une période (qui joignent les écritures à leur pièce).
 */
export const MANUAL_CREDIT_KIND = 'manual_credit';

/**
 * LA NATURE d'une écriture du grand livre — ce que le détail de la cagnotte nomme, à la caisse comme au siège.
 * ⚠️ `unknown_zero` n'est pas un cas de fonctionnement : une écriture à zéro sur les trois colonnes est un défaut d'écriture,
 *    nommé au lieu de passer pour un gain nul.
 */
export type LedgerMovementKind = 'earn' | 'welcome' | 'manual_credit' | 'spend' | 'voucher' | 'voucher_void' | 'clawback' | 'discount_refund' | 'unknown_zero';

/** Une écriture telle que sa nature se lit : ses montants, sa pièce, sa nature écrite. */
export interface LedgerMovementRow extends LedgerEntry {
  /** La pièce rattachée — `null` pour un bonus de bienvenue ou un crédit posé à la main. */
  saleId: string | null;
  /** La nature écrite (`MANUAL_CREDIT_KIND`, `RETURN_CORRECTION_KIND`) — `null` : elle se déduit des montants. */
  entryKind?: string | null;
}

/**
 * LA NATURE D'UNE ÉCRITURE, dans l'ordre où les colonnes se distinguent — UNE règle pour la caisse et le siège.
 *
 * ⚠️ DEUX LECTURES DE LA MÊME LIGNE ONT EXISTÉ : la caisse nommait « Bonus de bienvenue » ce que le siège nommait « Crédit
 *    de cagnotte », et un débit d'une reprise legacy se lisait « gains utilisés » d'un côté, « reprise » de l'autre. Le
 *    même client lisait deux histoires de la même cagnotte selon l'écran.
 * ⚠️ LA NATURE ÉCRITE PASSE AVANT LES MONTANTS : un crédit posé à la main n'a pas de vente, ses montants le feraient lire
 *    « Bonus de bienvenue ».
 * ⚠️ LE BON D'ACHAT PASSE ENSUITE : c'est la seule écriture qui laisse crédit ET débit à zéro, donc la seule qu'une cascade
 *    « crédit sinon débit » range au mauvais endroit. Une émission NÉGATIVE est un bon défait par l'annulation de sa vente.
 * ⚠️ UN DÉBIT QUI PORTE UN MONTANT RENDU, sans crédit, est une reprise de gain écrite en débit (la reprise legacy l'écrivait
 *    ainsi) ; la caisse écrit la sienne en crédit négatif. Les deux sont des reprises.
 * ⚠️ UN GAIN SE RECONNAÎT À SA PIÈCE OU À SON MONTANT D'ACHAT : une ligne reprise sans pièce retrouvée garde son achat, et
 *    n'est pas un bonus de bienvenue. Un crédit sans pièce, sans achat et sans nature écrite est le bonus de bienvenue —
 *    la seule écriture qui s'écrive ainsi.
 * ⚠️ Une écriture qui porterait deux colonnes à la fois (jamais produite) serait nommée par la première reconnue ; son
 *    MONTANT reste exact — le nom approxime, le chiffre jamais.
 */
export function ledgerMovementKind(row: LedgerMovementRow): LedgerMovementKind {
  if (row.entryKind === MANUAL_CREDIT_KIND) return 'manual_credit';
  const voucher = row.voucherIssuedCents ?? 0;
  if (voucher > 0) return 'voucher';
  if (voucher < 0) return 'voucher_void';
  if ((row.debitCents || 0) > 0) return (row.returnAmountCents ?? 0) > 0 && (row.creditCents || 0) === 0 ? 'clawback' : 'spend';
  if ((row.creditCents || 0) < 0) return 'clawback';
  if (isReturnedDiscount(row)) return 'discount_refund';
  if ((row.creditCents || 0) > 0) return row.saleId || (row.purchaseAmountCents ?? 0) > 0 ? 'earn' : 'welcome';
  return 'unknown_zero';
}

/**
 * LA CAGNOTTE OFFERTE — ce que le magasin a donné sans achat : les bonus de bienvenue et les crédits posés à la main.
 * ⚠️ C'EST UNE PART DE « ACQUIS » (`loyaltyPeriodTotals`), jamais un montant à y ajouter : les statistiques la disent à
 *    côté de ce qui a été distribué pour que le cadeau du magasin se lise, pas pour le compter deux fois.
 * ⚠️ LA NATURE VIENT DE `ledgerMovementKind` : un crédit posé à la main n'a pas de vente, et c'est sa nature écrite qui le
 *    distingue d'un bonus de bienvenue — les deux sont offerts.
 */
export function offeredCents(rows: readonly LedgerMovementRow[]): number {
  return rows.reduce((sum, row) => {
    const kind = ledgerMovementKind(row);
    return kind === 'welcome' || kind === 'manual_credit' ? sum + (row.creditCents || 0) : sum;
  }, 0);
}

/**
 * LA CAGNOTTE ÉTEINTE PAR UN EFFACEMENT (RGPD) — le débit que le siège appose quand une fiche est anonymisée, pour que le
 * solde du grand livre tombe à zéro avec l'identité qui pouvait le réclamer.
 * ⚠️ CE N'EST PAS UNE CONSOMMATION : rien n'a été rendu au client. Comptée avec les débits d'une période, elle ferait monter
 *    la cagnotte « utilisée » du jour de l'effacement.
 * ⚠️ RECONNUE À SON IDENTIFIANT : ses montants sont ceux d'un débit ordinaire, et l'identifiant est déterministe — une fiche
 *    ne s'anonymise qu'une fois. Son seul écrivain le prend ici (`erasureForfeitEntryId`), son lecteur aussi : les écritures
 *    déjà posées se reconnaissent sans migration.
 */
export const ERASURE_FORFEIT_ID_PREFIX = 'llg-anon-';

/** L'identifiant de l'écriture qui éteint la cagnotte d'une fiche anonymisée. */
export function erasureForfeitEntryId(clientId: string): string {
  return `${ERASURE_FORFEIT_ID_PREFIX}${clientId}`;
}

/** Cette écriture éteint-elle la cagnotte d'une fiche anonymisée ? */
export function isErasureForfeit(entryId: string): boolean {
  return entryId.startsWith(ERASURE_FORFEIT_ID_PREFIX);
}

export {
  returnLoyaltyReversal, returnReversalEntryIds, RETURN_CORRECTION_KIND,
  type LoyaltyReversal, type OriginLoyaltyLine, type ReturnLoyaltyBasis, type ReturnedLoyaltyLine,
} from './return-reversal';
